import {
  Box,
  Card,
  Chip,
  Collapse,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import { MdExpandMore, MdWarningAmber } from "react-icons/md";
import {
  countBundleObserved,
  countBundleResolved,
  articleComplete,
} from "../../../helpers/incomeEntry";
import { typographyStyles } from "constants/styles";
import { Article, IIncomeEntryArticleBundle, IIncomeEntryArticle } from "types";
import IncomeEntryArticleBundleCard from "../Bundle/IncomeEntryArticleBundleCard";
import { useCallback } from "react";

interface IncomeEntryArticleCardProps {
  incomeEntryArticle: IIncomeEntryArticle;
  open: boolean;
  storeId: number | null;
  onToggleOpen: (id: number) => void;
  onUpdateArticle: (article: IIncomeEntryArticle) => void;
}

export default function IncomeEntryArticleCard({
  incomeEntryArticle,
  open,
  storeId,
  onToggleOpen,
  onUpdateArticle,
}: IncomeEntryArticleCardProps) {
  const complete = articleComplete(incomeEntryArticle);
  const resolved = countBundleResolved(incomeEntryArticle);
  const observed = countBundleObserved(incomeEntryArticle);
  const total = incomeEntryArticle.bundles.length;

  const article: Article | undefined = incomeEntryArticle.article;

  const borderColor = complete
    ? "success.main"
    : open
      ? "secondary.main"
      : "divider";

  const handleBundleSubmit = useCallback(
    (updatedBundle: IIncomeEntryArticleBundle) => {
      const nextArticle: IIncomeEntryArticle = {
        ...incomeEntryArticle,
        bundles: incomeEntryArticle.bundles.map((b) =>
          b.id === updatedBundle.id ? updatedBundle : b
        ),
      };
      onUpdateArticle(nextArticle);
    },
    [incomeEntryArticle, onUpdateArticle]
  );

  return (
    <Card
      variant="outlined"
      sx={{
        mb: 1.5,
        borderWidth: 1.5,
        borderColor,
        transition: "border-color .2s",
      }}
    >
      <Box
        component="button"
        onClick={() => onToggleOpen(incomeEntryArticle.id)}
        aria-expanded={open}
        sx={{
          position: open ? "sticky" : "static",
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          p: 2,
          background: "none",
          border: "none",
          cursor: "pointer",
          textAlign: "left",
          color: "inherit",
        }}
      >
        <Box
          aria-hidden
          sx={{
            width: 12,
            height: 12,
            borderRadius: "50%",
            flexShrink: 0,
            bgcolor: complete
              ? "success.main"
              : observed > 0
                ? "warning.main"
                : "text.disabled",
          }}
        />

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography fontWeight={700} noWrap>
            {incomeEntryArticle.external_code}
          </Typography>
          <Typography
            variant="body2"
          >
            {article?.description}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={ typographyStyles.monospace }
          >
            {article?.container?.description} · {incomeEntryArticle.amount.toLocaleString("es-AR")} unid.
          </Typography>
        </Box>

        {observed > 0 && (
          <Chip
            size="small"
            color="warning"
            variant="outlined"
            icon={<MdWarningAmber />}
            label={observed}
            sx={{ ...typographyStyles.monospace, flexShrink: 0, pl: 0.5 }}
          />
        )}

        <Chip
          size="small"
          color={complete ? "success" : "secondary"}
          variant="outlined"
          label={`${resolved}/${total}`}
          sx={{ ...typographyStyles.monospace, flexShrink: 0 }}
        />

        <MdExpandMore
          size={20}
          style={{
            flexShrink: 0,
            transform: open ? "rotate(180deg)" : "none",
            transition: "transform .18s",
          }}
        />
      </Box>

      <Collapse in={open} unmountOnExit>
        <Divider />
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", px: 2, pt: 1, pb: 0.5, letterSpacing: 0.6 }}
        >
          N° FARDO · PRODUCCIÓN
        </Typography>
        <Stack 
          gap={1}
          p={1}
        >
        {incomeEntryArticle.bundles.map((b) => (
          <IncomeEntryArticleBundleCard
            key={b.id}
            bundle={b}
            weightKgPerItem={incomeEntryArticle.article?.equivalence?.weight_kg_per_item}
            onSubmit={handleBundleSubmit}
            storeId={storeId}
          />
        ))}
        </Stack>
      </Collapse>
    </Card>
  );
}
